export interface ChecklistTemplate {
  templateId?: number;
  name: string;
  isActive: boolean;
  enteredBy: string;
  modifiedBy: string;
  enteredDate?: Date;
  modifiedDate?: Date;
}

export interface ChecklistTemplateSection {
  sectionId?: number;
  sectionName: string;
  checkListTemplateId?: number;
  isActive: boolean;
  sectionType: "Images" | "Videos" | "Checklist";
  enteredBy: string;
  modifiedBy: string;
  enteredDate?: Date;
  modifiedDate?: Date;
}

export interface ChecklistTemplateItem {
  itemId?: number;
  sectionId?: number;
  itemName: string;
  isSelected: boolean;
  isActive: boolean;
  enteredBy: string;
  modifiedBy: string;
  enteredDate?: Date;
  modifiedDate?: Date;
  questionType: "Text" | "Multi-Select" | "Single-Select" | "image" | "video";
  allowImages: boolean;
  allowComment: boolean;
}

export interface ChecklistTemplateItemOption {
  optionId?: number;
  itemId?: number;
  optionText: string;
  isActive: boolean;
}

// Database interfaces with transformed values
export interface ChecklistTemplateSectionDB {
  sectionId?: number;
  sectionName: string;
  checkListTemplateId?: number;
  isActive: boolean;
  sectionType: "image" | "video" | "checklist"; // Transformed values for DB
  enteredBy: string;
  modifiedBy: string;
  enteredDate?: Date;
  modifiedDate?: Date;
}

export interface ChecklistTemplateItemDB {
  itemId?: number;
  sectionId?: number;
  itemName: string;
  isSelected: boolean;
  isActive: boolean;
  enteredBy: string;
  modifiedBy: string;
  enteredDate?: Date;
  modifiedDate?: Date;
  questionType: "text" | "multiselect" | "singleselect" | "image" | "video"; // Transformed values for DB
  allowImages: boolean;
  allowComment: boolean;
}

export interface ChecklistTemplateRequest {
  template: ChecklistTemplate;
  sections: ChecklistTemplateSectionDB[]; // Use DB interface
  items: ChecklistTemplateItemDB[]; // Use DB interface
  options: ChecklistTemplateItemOption[];
}

export interface ChecklistTemplateResponse {
  templateId: number;
  success: boolean;
  message: string;
}

export interface ChecklistTemplateFullResponse {
  template: ChecklistTemplate;
  sections: UISection[];
}

/***********************
 * Transformation utilities for database storage *
 ***********************/

/**
 * Transforms sectionType from UI display value to database storage value
 * UI: "Images" | "Videos" | "Checklist"
 * DB: "image" | "video" | "checklist"
 */
export const transformSectionTypeForDB = (sectionType: "Images" | "Videos" | "Checklist"): "image" | "video" | "checklist" => {
  switch (sectionType) {
    case "Images":
      return "image";
    case "Videos":
      return "video";
    case "Checklist":
      return "checklist";
    default:
      return "checklist"; // fallback
  }
};

/**
 * Transforms questionType from UI display value to database storage value
 * UI: "Text" | "Multi-Select" | "Single-Select" | "image" | "video"
 * DB: "text" | "multiselect" | "singleselect" | "image" | "video"
 */
export const transformQuestionTypeForDB = (questionType: "Text" | "Multi-Select" | "Single-Select" | "image" | "video"): "text" | "multiselect" | "singleselect" | "image" | "video" => {
  switch (questionType) {
    case "Text":
      return "text";
    case "Multi-Select":
      return "multiselect";
    case "Single-Select":
      return "singleselect";
    case "image":
    case "video":
      return questionType; // Already in correct format
    default:
      return "text"; // fallback
  }
};

/**
 * Transforms sectionType from database storage value to UI display value
 * DB: "image" | "video" | "checklist"
 * UI: "Images" | "Videos" | "Checklist"
 */
export const transformSectionTypeForUI = (sectionType: string): "Images" | "Videos" | "Checklist" => {
  switch (sectionType.toLowerCase()) {
    case "image":
      return "Images";
    case "video":
      return "Videos";
    case "checklist":
      return "Checklist";
    default:
      return "Checklist"; // fallback
  }
};

/**
 * Transforms questionType from database storage value to UI display value
 * DB: "text" | "multiselect" | "singleselect" | "image" | "video"
 * UI: "Text" | "Multi-Select" | "Single-Select" | "image" | "video"
 */
export const transformQuestionTypeForUI = (questionType: string): "Text" | "Multi-Select" | "Single-Select" | "image" | "video" => {
  switch (questionType.toLowerCase()) {
    case "text":
      return "Text";
    case "multiselect":
      return "Multi-Select";
    case "singleselect":
      return "Single-Select";
    case "image":
      return "image";
    case "video":
      return "video";
    default:
      return "Text"; // fallback
  }
};

/**
 * Transforms a complete template response from database format to UI format
 * This should be used when loading template data from the API
 */
export const transformTemplateResponseForUI = (dbResponse: any) => {
  if (!dbResponse) return null;
  
  return {
    ...dbResponse,
    sections: dbResponse.sections?.map((section: any) => ({
      ...section,
      sectionType: transformSectionTypeForUI(section.sectionType),
      items: section.items?.map((item: any) => ({
        ...item,
        questionType: transformQuestionTypeForUI(item.questionType),
      })) || [],
    })) || [],
  };
};

/**
 * Transforms the full template response from GetChecklistTemplate API to UI format
 * This includes template, sections with items, and options nested properly
 */
export const transformFullTemplateResponseForUI = (dbResponse: any): ChecklistTemplateFullResponse | null => {
  if (!dbResponse) return null;
  
  const template: ChecklistTemplate = {
    templateId: dbResponse.template?.templateId,
    name: dbResponse.template?.name || "",
    isActive: dbResponse.template?.isActive ?? true,
    enteredBy: dbResponse.template?.enteredBy || "system",
    modifiedBy: dbResponse.template?.modifiedBy || "system",
    enteredDate: dbResponse.template?.enteredDate,
    modifiedDate: dbResponse.template?.modifiedDate,
  };

  const sections: UISection[] = (dbResponse.sections || []).map((section: any) => ({
    sectionId: section.sectionId,
    sectionName: section.sectionName || "",
    checkListTemplateId: section.checkListTemplateId,
    isActive: section.isActive ?? true,
    sectionType: transformSectionTypeForUI(section.sectionType),
    enteredBy: section.enteredBy || "system",
    modifiedBy: section.modifiedBy || "system",
    enteredDate: section.enteredDate,
    modifiedDate: section.modifiedDate,
    isFixed: section.sectionType?.toLowerCase() === "image" || section.sectionType?.toLowerCase() === "video",
    items: (section.items || []).map((item: any) => {
      const questionType = transformQuestionTypeForUI(item.questionType);
      const options = (item.options || []).map((option: any) => ({
        optionId: option.optionId,
        itemId: option.itemId,
        optionText: option.optionText || "",
        isActive: option.isActive ?? true,
      }));
      return {
        itemId: item.itemId,
        sectionId: item.sectionId,
        itemName: item.itemName || "",
        isSelected: item.isSelected ?? false,
        isActive: item.isActive ?? true,
        enteredBy: item.enteredBy || "system",
        modifiedBy: item.modifiedBy || "system",
        enteredDate: item.enteredDate,
        modifiedDate: item.modifiedDate,
        questionType,
        allowImages: item.allowImages ?? false,
        allowComment: item.allowComment ?? false,
        options,
        multiSelectOptions: questionType === "Multi-Select" ? options : [],
        singleSelectOptions: questionType === "Single-Select" ? options : [],
      };
    }),
  }));

  return { template, sections };
};

/***********************
 * UI-only state shapes *
 ***********************/
export type SectionType = ChecklistTemplateSection["sectionType"];
export type QuestionType = ChecklistTemplateItem["questionType"];

export interface UIOption extends ChecklistTemplateItemOption {
  optionId: number;
}

export interface UIItem extends ChecklistTemplateItem {
  itemId: number;
  sectionId: number;
  options: UIOption[];
  multiSelectOptions: UIOption[];
  singleSelectOptions: UIOption[];
}

export interface UISection extends ChecklistTemplateSection {
  sectionId: number;
  items: UIItem[];
  /** fixed images/videos */ isFixed?: boolean;
}

/*******************
 * Temp ID helpers  *
 *******************/
let tempIdCounter = -1;
export const getTempId = () => tempIdCounter--; // always returns a unique negative number

/*******************
 * Factory helpers  *
 *******************/
export const emptyTemplate = (): ChecklistTemplate => ({
  templateId: getTempId(),
  name: "",
  isActive: true,
  enteredBy: "system",
  modifiedBy: "system",
});

export const createSection = (type: SectionType, name = ""): UISection => ({
  sectionId: getTempId(),
  sectionName: name,
  checkListTemplateId: undefined,
  isActive: true,
  sectionType: type,
  enteredBy: "system",
  modifiedBy: "system",
  items: [],
});

export const createItem = (
  sectionId: number,
  sectionType: SectionType
): UIItem => {
  const q: QuestionType =
    sectionType === "Images"
      ? "image"
      : sectionType === "Videos"
      ? "video"
      : "Text";
  return {
    itemId: getTempId(),
    sectionId,
    itemName: "",
    isSelected: false,
    isActive: true,
    enteredBy: "system",
    modifiedBy: "system",
    questionType: q,
    allowImages: false,
    allowComment: false,
    options: [],
    multiSelectOptions: [],
    singleSelectOptions: [],
  };
};

export const createOption = (itemId: number): UIOption => ({
  optionId: getTempId(),
  itemId,
  optionText: "",
  isActive: true,
});

/****************
 * Validations   *
 ****************/
export interface ValidationErrors {
  templateName?: string;
  sectionErrors: Record<number, { sectionName?: string }>;
  itemErrors: Record<number, { itemName?: string; options?: string }>;
  optionErrors: Record<number, { optionText?: string }>;
}

export const validateAll = (
  template: ChecklistTemplate,
  sections: UISection[]
): ValidationErrors => {
  const errs: ValidationErrors = {
    sectionErrors: {},
    itemErrors: {},
    optionErrors: {},
  };
  if (!template.name.trim()) errs.templateName = "Template name is required";

  sections.forEach((s) => {
    // Section name required for all three top-level segments and checklist sections
    if (!s.sectionName.trim())
      errs.sectionErrors[s.sectionId] = {
        sectionName: "Section name is required",
      };

    s.items.forEach((it) => {
      const iErr: { itemName?: string; options?: string } = {};
      if (!it.itemName.trim()) iErr.itemName = "Item name is required";

      if (
        s.sectionType === "Checklist" &&
        (it.questionType === "Single-Select" ||
          it.questionType === "Multi-Select")
      ) {
        const optionsToValidate = it.questionType === "Multi-Select" 
          ? it.multiSelectOptions 
          : it.singleSelectOptions;
        const nonEmpty = optionsToValidate.filter(
          (o) => o.isActive && o.optionText.trim()
        );
        if (nonEmpty.length < 2)
          iErr.options = "Add one more option (minimum 2 options required)";
        optionsToValidate.forEach((o) => {
          if (!o.optionText.trim())
            errs.optionErrors[o.optionId] = {
              optionText: "Option text is required",
            };
        });
      }

      if (Object.keys(iErr).length) errs.itemErrors[it.itemId] = iErr;
    });
  });

  return errs;
};

export const hasAnyErrors = (e: ValidationErrors) =>
  !!(
    e.templateName ||
    Object.keys(e.sectionErrors).length ||
    Object.keys(e.itemErrors).length ||
    Object.keys(e.optionErrors).length
  );
